import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';

import { css, StyleSheet } from 'aphrodite';
import { useHistory, useLocation } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSearch } from '@fortawesome/free-solid-svg-icons';

import useGet from 'lib/hooks/useGet';
import placeholderProductImage from 'images/placeholder-img-product-v2.svg';
import { addReferrerToUrl } from 'lib/utils/navigationUtils';
import { getURL, GLOBAL_PRODUCTS_BY_SEARCH } from 'lib/networking/endpoints';
import { ProductType } from 'lib/enums/ProductType';
import { NAVIGATION_SIDE_BAR_WIDTH } from 'lib/constants';
import {
  BG_2,
  BORDER_2,
  FG_1,
  FG_3,
  FONT_BODY,
  FONT_SIZE_BODY,
  FONT_SIZE_SMALL,
  FONT_SIZE_XS,
  FONT_TITLE,
  FONT_WEIGHT_EXTRABOLD,
  FONT_WEIGHT_MEDIUM,
  FONT_WEIGHT_SEMIBOLD,
  GLOBAL_NAV_HEIGHT,
  PRIMARY_COLOR,
  PRIMARY_LIGHT_RGBA,
  RADIUS_LG,
  RADIUS_MD,
  RADIUS_SM,
  SHADOW_LG,
  SPACE_2,
  SPACE_5,
  TOPBAR_HEIGHT,
  WHITE,
} from 'components/upb_viewer_beta/css/betaTokens';

const MIN_QUERY_LENGTH = 2;
const RESULT_LIMIT = 8;

function productImage(product) {
  return (
    product.thumbnail_url ||
    product.default_image_url ||
    product.propagated_thumbnail_image_url ||
    placeholderProductImage
  );
}

function BetaTopBar({ searchQuery, onSearchChange, navExpanded, acronym, title, searchAccessory }) {
  const history = useHistory();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);

  // `draft` is the text in the box; the search is committed (and the grid +
  // dropdown actually fetch) only when the user presses Enter. Semantic search
  // is too heavy to run on every keystroke, and partial-word queries produce
  // poor, flickering results — so typing is local-only until commit. The
  // committed term arrives back as the `searchQuery` prop from the parent.
  const [draft, setDraft] = useState(searchQuery || '');
  useEffect(() => {
    setDraft(searchQuery || '');
  }, [searchQuery]);

  const trimmed = (searchQuery || '').trim();
  const skipSearch = trimmed.length < MIN_QUERY_LENGTH;

  const { data: { data: products = [] } = {}, loading } = useGet(
    getURL(GLOBAL_PRODUCTS_BY_SEARCH, {}),
    {
      offset: 0,
      limit: RESULT_LIMIT,
      search_query: trimmed,
      filter_products_with_active_dp_products: true,
    },
    skipSearch,
  );

  const commitSearch = () => {
    if (onSearchChange) onSearchChange(draft.trim());
    setOpen(false);
  };

  useEffect(() => {
    if (!open) return undefined;
    const handler = e => {
      if (rootRef.current && !rootRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const goToProduct = product => {
    const base = `/product-details/${product.fsa_id || product.id}/${ProductType.GLOBAL}`;
    history.push(addReferrerToUrl(base, `${location.pathname}${location.search}`));
    setOpen(false);
  };

  // Only show the suggestion dropdown for the committed term — never for an
  // in-progress edit (that would fetch/flicker per keystroke again).
  const showDropdown = open && !skipSearch && draft.trim() === trimmed;

  return (
    <div className={css(styles.topBar, navExpanded && styles.topBarNavOpen)}>
      {/* Brand mark */}
      <div className={css(styles.brand)}>
        <div className={css(styles.brandMark)}>C+</div>
        <div className={css(styles.brandName)}>Cut+Dry</div>
      </div>

      {/* Divider */}
      <div className={css(styles.divider)} />

      {/* Page label */}
      <div className={css(styles.pageLabel)}>
        <span>{acronym}</span>
        <span> &nbsp;·&nbsp; </span>
        <span className={css(styles.pageLabelStrong)}>{title}</span>
      </div>

      {/* Spacer */}
      <div className={css(styles.spacer)} />

      {/* Optional control rendered just left of the search box (e.g. the MSV
          Products/Manufacturers result toggle). */}
      {searchAccessory && <div className={css(styles.searchAccessory)}>{searchAccessory}</div>}

      {/* Search input + autocomplete dropdown */}
      <div className={css(styles.searchWrap)} ref={rootRef}>
        <div className={css(styles.searchBox, showDropdown && styles.searchBoxOpen)}>
          <FontAwesomeIcon icon={faSearch} className={css(styles.searchIcon)} />
          <input
            className={css(styles.searchInput)}
            type="text"
            placeholder="Search by name, GTIN, SKU… (press Enter)"
            value={draft}
            onChange={e => setDraft(e.target.value)}
            onKeyDown={e => {
              if (e.key === 'Enter') {
                e.preventDefault();
                commitSearch();
              }
            }}
            onFocus={() => setOpen(true)}
          />
          <span className={css(styles.searchKbd)}>↵</span>
        </div>

        {showDropdown && (
          <div className={css(styles.dropdown)}>
            {loading && <div className={css(styles.dropdownMsg)}>Searching…</div>}
            {!loading && products.length === 0 && (
              <div className={css(styles.dropdownMsg)}>No products found</div>
            )}
            {!loading &&
              products.map(product => (
                <div
                  key={product.id}
                  className={css(styles.resultRow)}
                  onMouseDown={() => goToProduct(product)}
                >
                  <img src={productImage(product)} alt="" className={css(styles.resultImg)} />
                  <div className={css(styles.resultInfo)}>
                    <span className={css(styles.resultName)}>{product.name}</span>
                    {product.gtin && (
                      <span className={css(styles.resultMeta)}>{`GTIN ${product.gtin}`}</span>
                    )}
                  </div>
                </div>
              ))}
          </div>
        )}
      </div>
    </div>
  );
}

const styles = StyleSheet.create({
  topBar: {
    position: 'fixed',
    top: GLOBAL_NAV_HEIGHT,
    left: 0,
    right: 0,
    height: TOPBAR_HEIGHT,
    backgroundColor: WHITE,
    borderBottom: `1px solid ${BORDER_2}`,
    display: 'flex',
    alignItems: 'center',
    gap: 14,
    padding: `0 ${SPACE_5}px`,
    zIndex: 100,
  },

  // When the global nav drawer is open, shift past it (left = rail width) so the
  // fixed bar stays aligned with the body content that FullScreenMainContainer pushes.
  topBarNavOpen: {
    left: NAVIGATION_SIDE_BAR_WIDTH,
  },

  brand: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
  },

  brandMark: {
    width: 32,
    height: 32,
    borderRadius: RADIUS_MD,
    backgroundColor: PRIMARY_COLOR,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontFamily: FONT_TITLE,
    fontWeight: 900,
    fontSize: 14,
    color: WHITE,
    letterSpacing: '-0.02em',
  },

  brandName: {
    fontFamily: FONT_TITLE,
    fontWeight: FONT_WEIGHT_EXTRABOLD,
    fontSize: 15,
    color: FG_1,
    letterSpacing: '-0.01em',
  },

  divider: {
    width: 1,
    height: 24,
    backgroundColor: BORDER_2,
    margin: '0 4px',
    flexShrink: 0,
  },

  pageLabel: {
    fontFamily: FONT_BODY,
    fontWeight: FONT_WEIGHT_SEMIBOLD,
    fontSize: FONT_SIZE_SMALL,
    color: FG_3,
    textTransform: 'uppercase',
    letterSpacing: '0.08em',
    whiteSpace: 'nowrap',
  },

  pageLabelStrong: {
    color: FG_1,
  },

  spacer: {
    flex: 1,
  },

  searchAccessory: {
    flexShrink: 0,
  },

  searchWrap: {
    position: 'relative',
    width: 360,
    flexShrink: 0,
  },

  searchBox: {
    display: 'flex',
    alignItems: 'center',
    gap: SPACE_2,
    backgroundColor: BG_2,
    border: `1px solid ${BORDER_2}`,
    borderRadius: RADIUS_MD,
    padding: '8px 12px',
    height: 38,
    ':focus-within': {
      borderColor: PRIMARY_COLOR,
      boxShadow: `0 0 0 3px ${PRIMARY_LIGHT_RGBA}`,
      backgroundColor: WHITE,
    },
  },

  searchBoxOpen: {
    backgroundColor: WHITE,
  },

  searchIcon: {
    fontSize: 16,
    color: FG_3,
    flexShrink: 0,
    opacity: 0.55,
  },

  searchInput: {
    flex: 1,
    border: 'none',
    background: 'transparent',
    outline: 'none',
    fontFamily: FONT_BODY,
    fontSize: FONT_SIZE_BODY,
    color: FG_1,
    minWidth: 0,
    ':focus': {
      outline: 'none',
    },
    '::placeholder': {
      color: FG_3,
    },
  },

  searchKbd: {
    fontFamily: FONT_BODY,
    fontWeight: FONT_WEIGHT_SEMIBOLD,
    fontSize: 11,
    color: FG_3,
    border: `1px solid ${BORDER_2}`,
    borderRadius: RADIUS_SM,
    padding: '1px 6px',
    backgroundColor: WHITE,
    flexShrink: 0,
  },

  dropdown: {
    position: 'absolute',
    top: 'calc(100% + 6px)',
    left: 0,
    right: 0,
    backgroundColor: WHITE,
    border: `1px solid ${BORDER_2}`,
    borderRadius: RADIUS_LG,
    boxShadow: SHADOW_LG,
    overflow: 'hidden',
    maxHeight: 380,
    overflowY: 'auto',
    zIndex: 30,
    padding: '4px 0',
  },

  dropdownMsg: {
    padding: '14px 16px',
    fontFamily: FONT_BODY,
    fontSize: FONT_SIZE_SMALL,
    color: FG_3,
    textAlign: 'center',
  },

  resultRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    padding: '8px 14px',
    cursor: 'pointer',
    ':hover': {
      backgroundColor: BG_2,
    },
  },

  resultImg: {
    width: 36,
    height: 36,
    borderRadius: RADIUS_SM,
    objectFit: 'contain',
    backgroundColor: BG_2,
    border: `1px solid ${BORDER_2}`,
    flexShrink: 0,
  },

  resultInfo: {
    display: 'flex',
    flexDirection: 'column',
    minWidth: 0,
  },

  resultName: {
    fontFamily: FONT_BODY,
    fontWeight: FONT_WEIGHT_MEDIUM,
    fontSize: FONT_SIZE_SMALL,
    color: FG_1,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },

  resultMeta: {
    fontFamily: FONT_BODY,
    fontWeight: FONT_WEIGHT_MEDIUM,
    fontSize: FONT_SIZE_XS,
    color: FG_3,
    marginTop: 1,
  },
});

BetaTopBar.propTypes = {
  searchQuery: PropTypes.string,
  onSearchChange: PropTypes.func,
  navExpanded: PropTypes.bool,
  acronym: PropTypes.string,
  title: PropTypes.string,
  searchAccessory: PropTypes.node,
};

BetaTopBar.defaultProps = {
  acronym: 'UPB',
  title: 'Universal Product Book',
  searchAccessory: null,
};

export default BetaTopBar;
